import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Zap } from 'lucide-react'
import useRushdropStore from '../store/useRushdropStore'
import useAppStore from '../../../store/useAppStore'
import RushDropHeader from '../components/RushDropHeader'
import RushDropTimer from '../components/RushDropTimer'
import RushDropStatus from '../components/RushDropStatus'
import RushDropTasks from '../components/RushDropTasks'
import RushDropPrizes from '../components/RushDropPrizes'
import RushDropLeaderboard from '../components/RushDropLeaderboard'
import RushDropWinners from '../components/RushDropWinners'
import RushDropPromo from '../components/RushDropPromo'
import RushDropWeekly from '../components/RushDropWeekly'
import RushDropRulesModal from '../components/RushDropRulesModal'
import Loader from '../../../components/Loader'
import useAudioManager from '../../../hooks/useAudioManager'

// Период поллинга /api/fast-event/ (общая часть ответа кешируется сервером на 10 сек)
const POLL_INTERVAL = 12000

/**
 * Главная страница режима Rush Drop (backend-модуль fast_event).
 * Состояния: enabled:false | soon | active | ending | finished.
 * Таймеры тикают локально и ресинкаются с каждым ответом поллинга;
 * при нуле состояние не переключаем сами — ждём смены state от сервера.
 */
const RushDrop = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data, isLoading, error, loadEvent } = useRushdropStore()
  const language = useAppStore((state) => state.language)
  const isGuest = useAppStore((state) => state.isGuest)
  const openLogin = useAppStore((state) => state.openLogin)
  const { playSound } = useAudioManager({ select: 'select' })
  const [isRulesOpen, setIsRulesOpen] = useState(false)
  const prevLanguageRef = useRef(language)

  // Первичная загрузка + polling + мгновенный рефетч при возврате во вкладку
  useEffect(() => {
    loadEvent()

    const interval = setInterval(() => {
      loadEvent({ silent: true })
    }, POLL_INTERVAL)

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadEvent({ silent: true })
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [loadEvent, isGuest])

  // После смены языка (POST /api/lang/) перезапрашиваем эвент —
  // тексты приходят на языке из профиля
  useEffect(() => {
    if (prevLanguageRef.current !== language) {
      prevLanguageRef.current = language
      loadEvent({ silent: true })
    }
  }, [language, loadEvent])

  // Описание: если рисуем отдельный блок промокода — убираем последний абзац
  // (текст про промокод уже дописан в конец description, чтобы не было дубля)
  const description = useMemo(() => {
    const raw = data?.event?.description || ''
    if (!raw) return ''
    const parts = raw.split('\n\n').filter(Boolean)
    if (data?.promo && parts.length > 1) {
      parts.pop()
    }
    return parts.join('\n\n')
  }, [data?.event?.description, data?.promo])

  // Первая загрузка
  if (isLoading && !data) {
    return <Loader message="" />
  }

  // Ошибка без данных
  if (error && !data) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-text-secondary">
          {t('rushdrop.error', 'Не удалось загрузить Rush Drop')}
        </p>
        <button
          type="button"
          onClick={() => loadEvent()}
          className="rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-6 py-2.5 text-sm font-semibold text-dark-primary"
        >
          {t('rushdrop.retry', 'Попробовать снова')}
        </button>
      </div>
    )
  }

  // Эвента нет / модуль выключен / эвент заархивирован — одно состояние
  if (!data || data.enabled === false) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent-primary/10">
          <Zap size={32} className="text-accent-primary/60" />
        </div>
        <div>
          <p className="font-bold text-white">
            {t('rushdrop.empty.title', 'Rush Drop сейчас нет')}
          </p>
          <p className="mt-1 text-sm text-text-secondary">
            {t('rushdrop.empty.hint', 'Эвенты появляются внезапно в прайм-тайм — следи за анонсами')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            playSound('select')
            navigate('/')
          }}
          className="flex items-center gap-2 rounded-xl border border-white/10 bg-surface px-5 py-2.5 text-sm font-semibold text-white transition-all active:scale-[0.98]"
        >
          <ArrowLeft size={16} />
          {t('rushdrop.empty.toHome', 'На главную')}
        </button>
      </div>
    )
  }

  const {
    state,
    event,
    tasks,
    prizes,
    rules,
    promo,
    winner_mode: winnerMode,
    prize_places: prizePlaces,
    place_shift: placeShift,
    participants,
    you,
    top5,
    winners,
    weekly
  } = data

  // Один универсальный таймер: смысл зависит от state
  const timerSeconds = state === 'soon'
    ? event?.starts_in_seconds
    : state === 'finished'
      ? event?.results_close_in_seconds
      : event?.ends_in_seconds

  const isFinished = state === 'finished'
  const showProgress = state !== 'soon'

  return (
    <div className="mx-auto max-w-[1100px] space-y-4 px-4 py-4 lg:px-10 lg:py-6">
      {isFinished ? (
        <div className="space-y-4">
          <RushDropHeader
            event={event}
            state={state}
            placeShift={placeShift}
            onOpenRules={() => setIsRulesOpen(true)}
          />

          {/* Sticky-таймер — главный элемент срочности, закреплён при скролле */}
          <div className="sticky top-3 z-30">
            <RushDropTimer seconds={timerSeconds} state={state} />
          </div>

          <div className="space-y-6">
            <RushDropWinners
              winners={winners}
              participants={participants}
              prizePlaces={prizePlaces}
              you={you}
            />
            {/* При финале без участников призы показываем серыми */}
            <RushDropPrizes prizes={prizes} dimmed={Number(participants) === 0} />
            {promo && <RushDropPromo promo={promo} />}
          </div>
        </div>
      ) : (
        // DOM-порядок совпадает с мобильной раскладкой: баннер, таймер, контент, правая колонка.
        // На десктопе правая колонка закреплена на все 3 строки сетки справа.
        <div className="space-y-4 lg:grid lg:grid-cols-12 lg:items-start lg:gap-6 lg:space-y-0">
          {/* Баннер: на десктопе уменьшен и убран в левую колонку */}
          <div className="lg:col-span-7">
            <RushDropHeader
              event={event}
              state={state}
              placeShift={placeShift}
              onOpenRules={() => setIsRulesOpen(true)}
            />
          </div>

          {/* Таймер: до старта эвента — крупный блок на всю ширину;
              после старта на мобилке — sticky-плашка, на десктопе — плашка в левой колонке */}
          <div className="lg:col-span-7">
            <div className="lg:hidden">
              {state === 'soon' ? (
                <RushDropTimer seconds={timerSeconds} state={state} variant="block" />
              ) : (
                <div className="sticky top-3 z-30">
                  <RushDropTimer seconds={timerSeconds} state={state} />
                </div>
              )}
            </div>
            <div className="hidden lg:block">
              <RushDropTimer
                seconds={timerSeconds}
                state={state}
                variant={state === 'soon' ? 'block' : 'bar'}
              />
            </div>
          </div>

          {/* Левая колонка: описание, свой статус, задания.
              До старта эвента задания скрыты везде — вместо них таймер выше */}
          <div className="space-y-4 lg:col-span-7">
            {description && (
              <p className="whitespace-pre-line rounded-2xl border border-white/10 bg-surface p-4 text-sm leading-relaxed text-text-secondary">
                {description}
              </p>
            )}
            {showProgress && (isGuest ? (
              <div className="rounded-2xl border border-white/10 bg-surface p-4 text-center">
                <p className="text-sm text-text-secondary">{t('guest.privateDescription')}</p>
                <button type="button" onClick={openLogin} className="mt-3 rounded-xl bg-accent-primary px-6 py-2.5 font-bold text-dark-primary">{t('guest.login')}</button>
              </div>
            ) : <RushDropStatus you={you} />)}
            {showProgress && (
              <RushDropTasks tasks={tasks} showProgress={!isGuest} />
            )}
            {/* Недельный розыгрыш: независим от state эвента (кроме finished),
                нет блока weekly в ответе — карточки нет вовсе */}
            <RushDropWeekly weekly={weekly} />
          </div>

          {/* Правая колонка: призы, лидерборд, промокод */}
          <div className="space-y-6 lg:col-span-5 lg:col-start-8 lg:row-span-3 lg:row-start-1 lg:sticky lg:top-24">
            <RushDropPrizes prizes={prizes} />
            {showProgress && (
              <RushDropLeaderboard
                top5={top5}
                you={you}
                participants={participants}
                winnerMode={winnerMode}
              />
            )}
            {promo && <RushDropPromo promo={promo} />}
          </div>
        </div>
      )}

      <RushDropRulesModal
        isOpen={isRulesOpen}
        onClose={() => setIsRulesOpen(false)}
        rules={rules}
      />
    </div>
  )
}

export default RushDrop
